import type {
  FunctionComponentWithChildren,
  UnknownRecord
} from '@bonsai-components/utility-types';
import {
  ModButton,
  type ModButtonProps
} from '@moderneinc/design-system-components';
import {
  Dialog,
  DialogContent,
  type DialogProps,
  Skeleton
} from '@mui/material';
import { type DocumentNode, parse, print as pretty, visit } from 'graphql';
import {
  bindDialog,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GRAPHQL_ENDPOINT_PATH } from '../../constants/general';
import { ConfigurationContext } from '../../contexts/config.context';
import { CollapseDialogIcon, ExpandDialogIcon } from '../../icons/icons';
import {
  GraphiQLContainer,
  GraphiQLGlobalStyles
} from '../styled-components/tools/graphiql.styled';
import { ClosableDialogTitle } from '../utilities/dialog/closable-dialog-title.component';
import { ExpandCollapse } from '../utilities/expand-collapse/expand-collapse.component';
import { lazyLoadFallback } from '../utilities/lazy-load-fallback/lazy-load-fallback.component';

export const GraphiQLLoading = lazyLoadFallback({
  noun: 'API explorer',
  height: '100%'
});

// PERF: the editor pulls @graphiql/* (~170 KB gzip) and this shell mounts on every
// route, so it loads with the dialog body — Dialog unmounts children while closed.
const GraphQLExplorerEditor = dynamic(
  () =>
    import('./graphql-explorer-editor.component').then(
      (module) => module.GraphQLExplorerEditor
    ),
  { ssr: false, loading: GraphiQLLoading }
);

/**
 * Removes fields with @client directive from a GraphQL document.
 * These are Apollo Client-only fields that won't work on the server.
 */
const stripClientFields = (doc: DocumentNode): DocumentNode => {
  return visit(doc, {
    Field(node) {
      const hasClientDirective = node.directives?.some(
        (d) => d.name.value === 'client'
      );
      if (hasClientDirective) {
        return null;
      }
      return undefined;
    }
  });
};

const toGraphQLComment = (text: string): string =>
  text
    .split('\n')
    .map((line) => (line ? `# ${line}` : '#'))
    .join('\n');

export type GraphQLExplorerOperationVariables<T = UnknownRecord> =
  | (() => T)
  | T
  | Promise<T>;

type GraphQLExplorerDialog = {
  operationDocument: DocumentNode | string;
  operationVariables?: GraphQLExplorerOperationVariables;
  endpointUrl?: string;
  instructions?: string;
};

type GraphQLExplorerProps = {
  slots?: {
    button?: React.JSX.Element;
  };
  slotProps?: {
    button?: Partial<ModButtonProps>;
    dialog?: Partial<DialogProps>;
    graphqlExplorer?: Partial<GraphQLExplorerDialog>;
  };
};

export const GraphQLExplorer: FunctionComponentWithChildren<
  GraphQLExplorerProps
> = ({ children, slots = {}, slotProps = {} }) => {
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'graphql-explorer'
  });

  // Destructure Button-specific props that shouldn't leak onto slot IconButtons
  const {
    sx: buttonSx,
    startIcon,
    endIcon,
    variant,
    href,
    size,
    ...sharedButtonProps
  } = slotProps?.button || {};

  return (
    <React.Fragment>
      {slots?.button ? (
        React.cloneElement(slots.button, {
          ...bindTrigger(popupState),
          ...sharedButtonProps,
          active: popupState.isOpen,
          sx: {
            ...(slots.button.props.sx || {}),
            ...buttonSx
          }
        })
      ) : (
        <ModButton
          title="Click to view how to run this recipe directly with GraphQL"
          startIcon={startIcon}
          endIcon={endIcon}
          variant={variant}
          href={href}
          size={size}
          {...sharedButtonProps}
          {...bindTrigger(popupState)}
          data-testid="graphql-explorer-button"
          sx={{
            textTransform: 'none',
            textAlign: 'left',
            ...buttonSx
          }}
        >
          {children || 'See how to run against the API'}
        </ModButton>
      )}
      <GraphQLExplorerDialog
        popupState={popupState}
        slotProps={{
          dialog: slotProps?.dialog || {},
          graphqlExplorer: slotProps?.graphqlExplorer || {}
        }}
      />
    </React.Fragment>
  );
};
type GraphQLExplorerDialogProps = {
  popupState: ReturnType<typeof usePopupState>;
  slotProps: {
    dialog?: Partial<DialogProps>;
    graphqlExplorer: Partial<GraphQLExplorerDialog>;
  };
};

const GraphQLExplorerDialog: FunctionComponentWithChildren<
  GraphQLExplorerDialogProps
> = ({ slotProps, popupState }) => {
  const { operationDocument, operationVariables, endpointUrl, instructions } =
    slotProps.graphqlExplorer;

  const [variables, setVariables] = useState<UnknownRecord | null>(null);

  useEffect(() => {
    if (!popupState.isOpen) {
      return;
    }
    if (operationVariables instanceof Promise) {
      operationVariables.then(setVariables);
    } else if (operationVariables instanceof Function) {
      setVariables(operationVariables());
    } else {
      setVariables(operationVariables);
    }
  }, [operationVariables, popupState.isOpen]);

  const { getApiGatewayUrl } = useContext(ConfigurationContext);
  const graphqlURL =
    endpointUrl || `${getApiGatewayUrl()}${GRAPHQL_ENDPOINT_PATH}`;

  const [isFullScreen, setIsFullScreen] = useState<boolean>(
    slotProps?.dialog?.fullScreen || false
  );

  // PERF: rendered (closed) with every explorer button; keep parse/print off that path.
  const initialQuery = useMemo(() => {
    const printedQuery = pretty(
      stripClientFields(
        typeof operationDocument === 'string'
          ? parse(operationDocument)
          : operationDocument
      )
    );
    // print() drops comments, so instructions are prepended after printing.
    return instructions
      ? `${toGraphQLComment(instructions)}\n\n${printedQuery}`
      : printedQuery;
  }, [operationDocument, instructions]);

  return (
    <Dialog
      fullWidth
      maxWidth={!isFullScreen ? 'xl' : undefined}
      {...(slotProps?.dialog || {})}
      {...bindDialog(popupState)}
      fullScreen={isFullScreen}
      data-testid="graphql-dialog"
    >
      <ClosableDialogTitle
        title={`Moderne API @ ${graphqlURL}`}
        onClose={popupState.close}
        secondaryButton={
          <ExpandCollapse
            color="default"
            isExpanded={isFullScreen}
            onClick={() => setIsFullScreen(!isFullScreen)}
            expandIcon={<ExpandDialogIcon />}
            collapseIcon={<CollapseDialogIcon />}
          />
        }
      />
      <DialogContent sx={{ p: 0 }}>
        <GraphiQLGlobalStyles />
        <GraphiQLContainer
          data-testid="graphql-editor"
          sx={{
            minHeight: isFullScreen ? '100%' : '80vh',
            height: isFullScreen ? '100%' : '80vh',
            maxWidth: isFullScreen ? '100%' : '90vw'
          }}
        >
          {operationVariables && !variables ? (
            <Skeleton width={400} height={400} />
          ) : (
            <GraphQLExplorerEditor
              url={graphqlURL}
              query={initialQuery}
              variables={
                variables ? JSON.stringify(variables, null, 2) : undefined
              }
            />
          )}
        </GraphiQLContainer>
      </DialogContent>
    </Dialog>
  );
};
